import {
  PrescriptionResponse,
  PurchaseItemResponse,
  SubscriptionDetailsResponse
} from "@9amhealth/openapi";
import type { FC } from "react";
import React from "react";
import { useBloc } from "@blac/react";
import { checkItemType } from "src/lib/checkItemType";
import { ParsedPurchaseItemResponse } from "src/lib/parsePurchaseItem";
import translate from "src/lib/translate";
import {
  ItemType,
  SubscriptionInterval
} from "src/state/TreatmentPlanCubit/TreatmentPlanCubit";
import PenImg from "src/ui/assets/images/iso-meds-pen.webp";
import pillBottle from "src/ui/assets/images/iso-meds-vial.jpg";
import { MedicationRowItem } from "src/ui/components/MedicationRowItem/MedicationRowItem";
import Collection from "src/ui/components/StyledComponents/Collection";
import CollectionTitle from "src/ui/components/StyledComponents/CollectionTitle";
import Translate from "src/ui/components/Translate/Translate";
import externalItemType = PurchaseItemResponse.externalItemType;
import asTemplateValue from "lib/asTemplateValue";
import { featureFlags } from "src/lib/featureFlags";
import { getsGlp1Through9am } from "src/lib/refillCohort";
import RefillRequirementsCubit from "src/state/RefillRequirementsCubit/RefillRequirementsCubit";
import {
  AppQueryPopupsController,
  AppPopup
} from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import Path from "src/constants/path";

export enum MetadataKeys {
  CATEGORIES = "medication.categories",
  UNIT_OF_DISPENSE = "medication.unit-of-dispense",
  INITIAL_APPLICATION_DATE = "medication.initial-application-date"
}

enum MedicationCategory {
  DIABETES_GLP1 = "DIABETES_GLP1"
}

export const MedicationSection: FC<{
  parsedMeds: ParsedPurchaseItemResponse[];
  subscription?: SubscriptionDetailsResponse;
}> = ({ parsedMeds, subscription }) => {
  // FEWEB-2501: concierge-care members open the refill hub from their GLP-1 med card, but only
  // when the renewal endpoint returned a result — without one the hub has nothing to show.
  const [{ data: renewal }] = useBloc(RefillRequirementsCubit);
  const canOpenRefillHub =
    featureFlags.refillDecouplingEnabled &&
    getsGlp1Through9am() &&
    Boolean(renewal);

  const openRefillHub = () => {
    AppQueryPopupsController.openPopup(AppPopup.prepareRefill, {
      additionalParameters: { returnUrl: `/${Path.app}/${Path.home}` }
    });
  };

  return (
    <>
      <CollectionTitle style={{ marginBottom: "0.75rem" }}>
        <Translate msg="prescriptionMedication" />
      </CollectionTitle>

      <Collection>
        {parsedMeds.map((item) => {
          const isPlaceholderMed = checkItemType(
            item.externalItemType,
            ItemType.PRELIMINARY_MEDICATION_SUGGESTION
          );

          const description = isPlaceholderMed
            ? translate("plan_overview_placeholder_medication_dosage")
            : item.intake;

          const inlineWarning = item.expiredAt
            ? translate("prescription_valid_missing_lab_values_alert")
            : "";

          const showRefill =
            item.externalItemType !== externalItemType.PRESCRIBED_MEDICATION;

          const categories = (item.metadata[MetadataKeys.CATEGORIES] ??
            []) as MedicationCategory[];
          const isGLP1 = categories.includes(MedicationCategory.DIABETES_GLP1);
          const showPenImage =
            isGLP1 &&
            [
              PrescriptionResponse.doseForm.MILLILITER,
              PrescriptionResponse.doseForm.PEN_NEEDLE
            ].includes(
              asTemplateValue(
                item.metadata[MetadataKeys.UNIT_OF_DISPENSE]
              ) as PrescriptionResponse.doseForm
            );

          return (
            <MedicationRowItem
              key={item.sku}
              sku={item.sku}
              item={item}
              image={showPenImage ? PenImg : pillBottle}
              imageAlt={
                showPenImage
                  ? translate("image.alt.glp1InjectionPen")
                  : translate("image.alt.medicationPillBottle")
              }
              duration={subscription?.renewalInterval as SubscriptionInterval}
              description={description}
              inlineWarning={inlineWarning}
              showRefill={showRefill}
              hidePrice
              hideRemove
              onClick={isGLP1 && canOpenRefillHub ? openRefillHub : undefined}
            />
          );
        })}
      </Collection>
    </>
  );
};
